if (!customElements.get('size-guide-drawer')) {
  class SizeGuideDrawer extends HTMLElement {
    constructor() {
      super();

      this.overlay = this.querySelector('[data-size-guide-overlay]');
      this.panel = this.querySelector('.size-guide-drawer__panel');
      this.closeButtons = Array.from(this.querySelectorAll('[data-size-guide-close]'));
      this.activeTrigger = null;

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onEscape = this.onEscape.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onTransitionEnd = this.onTransitionEnd.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);

      this.isInitialized = false;
    }

    connectedCallback() {
      if (this.mountToRoot()) return;
      if (this.isInitialized) {
        this.bindTriggerEvents();
        return;
      }

      this.bindTriggerEvents();
      this.overlay?.addEventListener('click', this.onOverlayClick);
      this.addEventListener('click', this.onCloseClick);
      this.isInitialized = true;
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.unbindTriggerEvents();
      this.overlay?.removeEventListener('click', this.onOverlayClick);
      this.removeEventListener('click', this.onCloseClick);
      document.removeEventListener('keydown', this.onEscape);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);

      if (this.classList.contains('active')) {
        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeTrigger);
      }

      this.isInitialized = false;
    }

    bindTriggerEvents() {
      this.unbindTriggerEvents();
      this.triggers = Array.from(document.querySelectorAll(`[data-size-guide-trigger][aria-controls="${this.id}"]`));
      this.triggers.forEach((trigger) => trigger.addEventListener('click', this.onTriggerClick));
    }

    unbindTriggerEvents() {
      this.triggers?.forEach((trigger) => trigger.removeEventListener('click', this.onTriggerClick));
    }

    onTriggerClick(event) {
      event.preventDefault();
      this.open(event.currentTarget);
    }

    onCloseClick(event) {
      if (!event.target.closest('[data-size-guide-close]')) return;
      event.preventDefault();
      this.close();
    }

    onOverlayClick(event) {
      event.preventDefault();
      this.close();
    }

    onEscape(event) {
      const eventCode = event.code ? event.code.toUpperCase() : '';
      if (event.key !== 'Escape' && eventCode !== 'ESCAPE') return;

      event.preventDefault();
      this.close();
    }

    onDocumentPointerDown(event) {
      if (!this.classList.contains('active')) return;
      if (!(event.target instanceof Element)) return;
      if (this.panel?.contains(event.target)) return;
      if (event.target.closest(`[data-size-guide-trigger][aria-controls="${this.id}"]`)) return;

      this.close();
    }

    updateTriggerState(isOpen) {
      const triggerState = isOpen ? 'true' : 'false';
      document.querySelectorAll(`[data-size-guide-trigger][aria-controls="${this.id}"]`).forEach((trigger) => {
        trigger.setAttribute('aria-expanded', triggerState);
      });
    }

    mountToRoot() {
      if (this.parentElement === document.body) {
        this.setAttribute('data-size-guide-root', 'true');
        return false;
      }

      const existingRootDrawer = Array.from(
        document.body.querySelectorAll('size-guide-drawer[data-size-guide-root="true"]')
      ).find((drawer) => drawer.id === this.id && drawer !== this);

      existingRootDrawer?.remove();
      this.setAttribute('data-size-guide-root', 'true');
      document.body.appendChild(this);

      return true;
    }

    open(trigger) {
      document.querySelectorAll('size-guide-drawer.active').forEach((drawer) => {
        if (drawer !== this && typeof drawer.close === 'function') drawer.close();
      });

      if (trigger) this.activeTrigger = trigger;

      this.hidden = false;
      this.updateTriggerState(true);
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onEscape);
      document.addEventListener('pointerdown', this.onDocumentPointerDown, true);

      requestAnimationFrame(() => {
        this.classList.add('active');
      });

      const focusTarget = this.closeButtons[0] || this.panel;
      if (this.panel && typeof trapFocus === 'function') {
        trapFocus(this.panel, focusTarget);
      } else {
        focusTarget?.focus();
      }
    }

    close() {
      if (this.hidden) return;

      this.classList.remove('active');
      this.updateTriggerState(false);
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onEscape);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.activeTrigger);
      } else {
        this.activeTrigger?.focus();
      }

      this.addEventListener('transitionend', this.onTransitionEnd, { once: true });
      setTimeout(() => {
        if (!this.classList.contains('active')) this.hidden = true;
      }, 350);
    }

    onTransitionEnd(event) {
      if (event.target !== this.panel) return;
      if (!this.classList.contains('active')) this.hidden = true;
    }
  }

  customElements.define('size-guide-drawer', SizeGuideDrawer);
}
